import type { Message } from '@/schemas/commons';
import type { AgentEvent } from '@/services/Conversations';

import type { TimelineItem } from './Transcript.types';

export const hasContent = (m: Message) =>
	m.content?.trim().length > 0 || (m.toolCalls?.length ?? 0) > 0 || (m.attachments?.length ?? 0) > 0;

const entryTime = (iso: string | null | undefined): number => {
	const ts = new Date(iso ?? '').getTime();

	return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
};

/** Same author, same role — the run WhatsApp collapses into one avatar. */
const sameAuthor = (a: TimelineItem | undefined, b: TimelineItem) =>
	a?.kind === 'message' && b.kind === 'message' && a.msg.role === b.msg.role && a.msg.authorName === b.msg.authorName;

/**
 * Interleaves messages, internal events and tool calls by timestamp, then marks
 * the boundaries of each run of consecutive messages from the same author.
 *
 * On equal timestamps events sort first: a turn's events happen before the AI
 * reply they feed. A tool call or an event between two messages **breaks the
 * group** — chronology wins over visual compression, otherwise the grouping
 * would imply the bot said two things in a row when it actually stopped to call
 * a tool in between.
 */
export const buildTimeline = (messages: Message[], events: AgentEvent[], showToolCalls: boolean): TimelineItem[] => {
	const ordered = [
		...messages.map((msg) => ({ time: entryTime(msg.createdAt), order: 1, msg, ev: null })),
		...events.map((ev) => ({ time: entryTime(ev.occurredAt), order: 0, msg: null, ev })),
	].sort((a, b) => a.time - b.time || a.order - b.order);

	const flat = ordered.flatMap<TimelineItem>((item) => {
		if (item.ev) return [{ kind: 'event', key: `ev-${item.ev.id}`, event: item.ev }];
		const msg = item.msg as Message;
		const tools =
			showToolCalls && msg.toolCalls
				? msg.toolCalls.map<TimelineItem>((tool) => ({ kind: 'tool', key: `tool-${tool.id}`, tool, msg }))
				: [];
		const bubble: TimelineItem[] =
			msg.content.trim().length > 0 || (msg.attachments?.length ?? 0) > 0
				? [{ kind: 'message', key: msg.id, msg, isFirstOfGroup: true, isLastOfGroup: true }]
				: [];

		return [...tools, ...bubble];
	});

	return flat.map((item, i) =>
		item.kind === 'message'
			? {
					...item,
					isFirstOfGroup: !sameAuthor(flat[i - 1], item),
					isLastOfGroup: !sameAuthor(flat[i + 1], item),
				}
			: item,
	);
};
